import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  CircularProgress,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import api from "../../utils/api";

export default function AddModal({ open, handleClose }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    adresse: "",
    Position: "",
    Téléphone: "",
    website: "",
    type_reservations: "",
    Deadline: "",
    Status: 1, // 1 for Actif, 0 for Inactif
    logo: null,
  });
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState("");

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === "checkbox" ? checked : value,
    }));
    
    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
    
    // Clear general error when any field changes
    if (generalError) {
      setGeneralError("");
    }
  };

  const handleLogoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData((prevData) => ({ 
        ...prevData, 
        logo: URL.createObjectURL(file),
        logoFile: file // Store the actual file for upload
      }));
    }
  };
  
  const handleSubmit = async () => {
    // Reset errors
    setErrors({});
    setGeneralError("");
    
    // Client-side validation for required fields
    const newErrors = {};
    if (!formData.name) newErrors.name = "Le nom est requis.";
    if (!formData.email) newErrors.email = "L'email est requis.";
    if (!formData.adresse) newErrors.adresse = "L'adresse est requise.";
    
    // Email format validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Veuillez entrer une adresse email valide.";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    try {
      // Create form data for multipart/form-data request (for file upload)
      const formDataToSend = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key !== 'logo' && key !== 'logoFile') {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add logo file if it exists
      if (formData.logoFile) {
        formDataToSend.append('Logo', formData.logoFile);
      }
      
      // Send create request
      await api.post('/societes', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        adresse: "",
        Position: "",
        Téléphone: "",
        website: "",
        type_reservations: "",
        Deadline: "",
        Status: 1,
        logo: null,
      });
      setErrors({});
      setGeneralError("");
      
      handleClose();
    } catch (error) {
      console.error('Error creating societe:', error);
      if (error.response && error.response.data) {
        // Handle validation errors from server
        if (typeof error.response.data === 'object') {
          // If it's a validation error with field-specific messages
          if (error.response.data.errors) {
            // Laravel ValidationException format
            setErrors(error.response.data.errors);
          } else {
            // Direct error object format
            const newErrors = {};
            Object.entries(error.response.data).forEach(([key, value]) => {
              if (Array.isArray(value)) {
                newErrors[key] = value[0]; // Take first error message for each field
              } else {
                newErrors[key] = value;
              }
            });
            setErrors(newErrors);
          }
          
          // Set general error if there's a message but no field-specific errors
          if (error.response.data.message && Object.keys(error.response.data).length <= 2) {
            setGeneralError(error.response.data.message);
          }
        } else {
          // If it's a string or other format
          setGeneralError(String(error.response.data));
        }
      } else if (error.message) {
        // Network or other errors
        setGeneralError(`Erreur: ${error.message}`);
      } else {
        setGeneralError('Erreur lors de la création de la société');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    '& .MuiOutlinedInput-root': {
      height: 35,
      '&:hover fieldset': { borderColor: '#000' },
      '&.Mui-focused fieldset': { borderColor: 'red' }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: 400, mr: 3 }}>
        AJOUTER UNE SOCIÉTÉ
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generalError}
          </Alert>
        )}
        <Typography variant="subtitle2">Nom <span style={{ color: 'red' }}>*</span></Typography>
        <TextField 
          fullWidth 
          name="name" 
          value={formData.name} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          required 
          error={Boolean(errors.name)}
          helperText={errors.name || ''}
        />
        <Typography variant="subtitle2">Email <span style={{ color: 'red' }}>*</span></Typography>
        <TextField 
          fullWidth 
          name="email" 
          value={formData.email} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          required 
          error={Boolean(errors.email)}
          helperText={errors.email || ''}
        />
        <Typography variant="subtitle2">Adresse <span style={{ color: 'red' }}>*</span></Typography>
        <TextField 
          fullWidth 
          name="adresse" 
          value={formData.adresse} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          required 
          error={Boolean(errors.adresse)}
          helperText={errors.adresse || ''}
        />
        <Typography variant="subtitle2">Position</Typography>
        <TextField 
          fullWidth 
          name="Position" 
          value={formData.Position} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.Position)}
          helperText={errors.Position || ''}
        />
        <Typography variant="subtitle2">Téléphone</Typography>
        <TextField 
          fullWidth 
          name="Téléphone" 
          value={formData.Téléphone} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.Téléphone)}
          helperText={errors.Téléphone || ''}
        />
        <Typography variant="subtitle2">Site Web</Typography>
        <TextField 
          fullWidth 
          name="website" 
          value={formData.website} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.website)}
          helperText={errors.website || ''}
        />
        <Typography variant="subtitle2">Type de réservations</Typography>
        <FormControl fullWidth sx={inputStyle} error={Boolean(errors.type_reservations)}>
          <Select name="type_reservations" value={formData.type_reservations} onChange={handleChange} displayEmpty>
            <MenuItem value="" disabled><em>Sélectionner</em></MenuItem>
            <MenuItem value="Standard">Standard</MenuItem>
            <MenuItem value="Premium">Premium</MenuItem>
            <MenuItem value="Hebdomadaire">Hebdomadaire</MenuItem>
          </Select>
          {errors.type_reservations && <FormHelperText>{errors.type_reservations}</FormHelperText>}
        </FormControl>
        <Typography variant="subtitle2">Deadline (Heure)</Typography>
        <TextField 
          fullWidth 
          type="time" 
          name="Deadline" 
          value={formData.Deadline} 
          onChange={handleChange} 
          variant="outlined" 
          sx={inputStyle} 
          error={Boolean(errors.Deadline)}
          helperText={errors.Deadline || ''}
        />
        <Typography variant="subtitle2">État</Typography>
        <FormControl fullWidth sx={inputStyle} error={Boolean(errors.Status)}>
          <Select name="Status" value={formData.Status} onChange={handleChange}>
            <MenuItem value={1}>Actif</MenuItem>
            <MenuItem value={0}>Inactif</MenuItem>
          </Select>
          {errors.Status && <FormHelperText>{errors.Status}</FormHelperText>}
        </FormControl>

        <Typography variant="subtitle2">Logo</Typography>
        <Box
          sx={{
            width: 200,
            height: 300,
            border: errors.Logo ? "1px solid #d32f2f" : "1px solid #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 2,
            cursor: "pointer",
            backgroundColor: "#f9f9f9",
          }}
          component="label"
        >
          {formData.logo ? (
            <img src={formData.logo} alt="Logo Preview" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 2 }} />
          ) : (
            <AddIcon fontSize="large" sx={{height:'150px', fontSize:'130px' }} color={errors.Logo ? "error" : "action"} />
          )}
          <input type="file" accept="image/*" hidden onChange={handleLogoChange} />
        </Box>
        {errors.Logo && <FormHelperText error>{errors.Logo}</FormHelperText>}
      </DialogContent>
      <Divider sx={{ mb: 1 }} />
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button variant="outlined" sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} startIcon={<CloseIcon />} onClick={handleClose}>
          Close
        </Button>
        <Button 
          variant="outlined" 
          sx={{ color: "red", borderColor: "red", textTransform: "none", "&:hover": { backgroundColor: "red", color: "#fff", borderColor: "red" } }} 
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}